import { useState, type FormEvent } from "react";
import { tEnglish as t } from "@buza/i18n";
import { api, type ApiErrorBody, type ZdSession } from "./api.js";

// Z01 ZD sign in (S-009, SEC-06): email and password, then the 6-digit authenticator code.
// Errors come from the api's rule message and never say which part was wrong. Entered data is
// kept after an error (spec 4.3, errors on every ZD screen).

type Step = "credentials" | "code";

export function SignIn({ onSignedIn }: { onSignedIn: (s: ZdSession) => void }) {
  const [step, setStep] = useState<Step>("credentials");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [fields, setFields] = useState<Record<string, string>>({});

  function showError(err: ApiErrorBody | undefined, status: number) {
    setFields(err?.fields ?? {});
    setError(status === 0 ? t("common.errors.no_connection") : err?.code === "VALIDATION" ? undefined : (err?.message ?? t("common.errors.validation")));
  }

  async function submitCredentials(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await api<{ next: "code" }>("/v1/zd/session", { method: "POST", body: { email, password } });
    setBusy(false);
    if (!r.ok) return showError(r.error, r.status);
    setError(undefined);
    setFields({});
    setCode("");
    setStep("code");
  }

  async function submitCode(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    const r = await api<ZdSession>("/v1/zd/session/mfa", { method: "POST", body: { code } });
    setBusy(false);
    if (r.ok) {
      setPassword("");
      return onSignedIn(r.data);
    }
    showError(r.error, r.status);
    // An expired or ended pending sign-in starts again from the first step, keeping the email.
    // The console is English only in Release 1 (TRN-01), so the rule message identifies it.
    if (r.error?.message === t("auth.signin.expired")) startAgain(false);
  }

  function startAgain(clearError = true) {
    setStep("credentials");
    setPassword("");
    setCode("");
    setFields({});
    if (clearError) setError(undefined);
  }

  return (
    <main className="z01">
      <h1>{t("console.signin.title")}</h1>
      <p>{t("console.signin.intro")}</p>
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}
      {step === "credentials" ? (
        <form onSubmit={submitCredentials} noValidate aria-label={t("console.signin.title")}>
          <label>
            {t("console.signin.email_label")}
            <input
              type="email"
              name="email"
              autoComplete="username"
              required
              maxLength={254}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={!!fields.email}
            />
          </label>
          {fields.email && <p className="field-error">{fields.email}</p>}
          <label>
            {t("console.signin.password_label")}
            <input
              type="password"
              name="password"
              autoComplete="current-password"
              required
              maxLength={200}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={!!fields.password}
            />
          </label>
          {fields.password && <p className="field-error">{fields.password}</p>}
          <button type="submit" disabled={busy}>
            {busy ? t("console.signin.working") : t("console.signin.continue")}
          </button>
        </form>
      ) : (
        <form onSubmit={submitCode} noValidate aria-label={t("console.signin.code_title")}>
          <h2>{t("console.signin.code_title")}</h2>
          <p>{t("console.signin.code_hint")}</p>
          <label>
            {t("console.signin.code_label")}
            <input
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              aria-invalid={!!fields.code}
            />
          </label>
          {fields.code && <p className="field-error">{fields.code}</p>}
          <button type="submit" disabled={busy}>
            {busy ? t("console.signin.working") : t("console.signin.verify")}
          </button>
          <button type="button" onClick={() => startAgain()} disabled={busy}>
            {t("console.signin.start_again")}
          </button>
        </form>
      )}
    </main>
  );
}
